import { useEffect, useState } from "react";
import {
  connectSocket,
  disconnectSocket,
  subscribeToImeis,
  getLastPositionsBatch,
} from "./socket";

export interface SgPosition {
  imei: string;
  timestamp: number;
  lat: number | null;
  lon: number | null;
  provider: string | null;
  plate: string | null;
  hasFix: boolean;
  raw: any;
}

export function normalize(p: any): SgPosition | null {
  const imei = p?.imei || p?.requestId;
  const rawTs =
    p?.last_comm_ts ??
    p?.point?.timeStamp ??
    p?.lastPosition?.timeStamp ??
    p?.timeStamp ??
    p?.timestamp ??
    p?.point?.timestamp;
  if (!imei || rawTs == null) return null;
  const ts = typeof rawTs === "number" ? rawTs : new Date(rawTs).getTime();
  if (Number.isNaN(ts)) return null;
  return {
    imei,
    timestamp: ts,
    lat: p?.point?.lat ?? p?.latitude ?? null,
    lon: p?.point?.lon ?? p?.longitude ?? null,
    provider: p?.provider ?? null,
    plate: p?.plate ?? null,
    hasFix: p?.has_fix ?? (p?.point?.lat != null && p?.point?.lon != null),
    raw: p,
  };
}

export function useSgDeviceConnections(imeis: string[]): Record<string, SgPosition> {
  const [positions, setPositions] = useState<Record<string, SgPosition>>({});
  const key = imeis.join(",");

  useEffect(() => {
    if (!imeis.length) return;

    const socket = connectSocket();

    const upsert = (p: any) => {
      const next = normalize(p);
      if (!next) return;
      setPositions((prev) => {
        const prevTs = prev[next.imei]?.timestamp;
        if (prevTs != null && prevTs >= next.timestamp) return prev;
        return { ...prev, [next.imei]: next };
      });
    };

    const onBatch = ({ items }: any = {}) => {
      for (const it of items || []) upsert(it);
    };

    const onConnect = () => {
      subscribeToImeis(imeis);
      getLastPositionsBatch(imeis, "tabla");
    };

    socket.on("connect", onConnect);
    socket.on("position", upsert);
    socket.on("last_positions", onBatch);

    if (socket.connected) onConnect();

    return () => {
      socket.off("connect", onConnect);
      socket.off("position", upsert);
      socket.off("last_positions", onBatch);
      disconnectSocket();
      setPositions({});
    };
  }, [key]);

  return positions;
}
